import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Home as HomeIcon } from "lucide-react";
import SEO from "../components/SEO";

export default function NotFound() {
  // Tell crawlers not to index 404 pages
  useEffect(() => {
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");

    return () => {
      // Restore default crawl behaviour when leaving the 404 page
      const r = document.querySelector('meta[name="robots"]');
      if (r) r.setAttribute("content", "index, follow");
    };
  }, []);

  return (
    <>
      <SEO
        title="404 — Page Not Found | District Wellness Center"
        description="The page you're looking for doesn't exist. Browse our DC dispensary locations, menu, and patient resources instead."
      />
      <section className="min-h-[80vh] bg-white relative overflow-hidden flex items-center" data-testid="not-found-page">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{backgroundImage: "linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)", backgroundSize: "60px 60px"}} />

        <div className="relative max-w-5xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">Error · 404</div>
            <h1 className="font-serif text-7xl sm:text-8xl lg:text-9xl leading-[0.9] tracking-tight">
              Page <em className="text-brand">not</em><br/>found.
            </h1>
            <p className="mt-8 text-lg text-ink-muted max-w-xl leading-relaxed">
              The page you're looking for doesn't exist — maybe it was moved, renamed, or never lived here at all.
              Let's get you back on track.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors"
                data-testid="404-home-btn"
              >
                <HomeIcon size={14} /> Back to Home
              </Link>
              <Link
                to="/menu"
                className="inline-flex items-center justify-center gap-2 bg-ink hover:bg-black text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors"
                data-testid="404-menu-btn"
              >
                Our Menu <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-black/10 p-8 bg-[#FAFAFA]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-4">Try these instead</div>
              <ul className="divide-y divide-black/10">
                {[
                  { to: "/locations", label: "Visit Our DC Dispensary" },
                  { to: "/delivery", label: "Same-Day Delivery" },
                  { to: "/areas-served", label: "Areas We Serve" },
                  { to: "/education", label: "Cannabis Education" },
                  { to: "/faq", label: "Frequently Asked Questions" },
                  { to: "/contact", label: "Contact DWC" },
                ].map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="flex items-center justify-between py-3 text-base text-ink hover:text-brand group"
                      data-testid={`404-link-${link.to.slice(1)}`}
                    >
                      <span>{link.label}</span>
                      <ArrowUpRight size={14} className="text-ink-muted group-hover:text-brand transition-colors" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
